// Enhanced ClientDashboard.js with completely unique CSS classes - Updated to use authUid
import React, { useEffect, useState, useCallback } from 'react';
import '../styles/ClientDashboard.css'; // New dedicated CSS file
import { auth, db } from '../firebase';
import { collection, query, where, getDocs, doc, updateDoc, addDoc } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';

import MessageForm from '../components/MessageForm';
import MessageLog from '../components/MessageLog';

export default function ClientDashboard() {
  const [orders, setOrders] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [clientDocId, setClientDocId] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [facebookEvents, setFacebookEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper functions for Facebook Events - matching FacebookEventsPage exactly
  const parseDate = useCallback(value => {
    if (!value) return null;
    if (value.seconds !== undefined && typeof value.toDate === 'function') {
      return value.toDate();
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    const dt = new Date(value);
    return isNaN(dt) ? null : dt;
  }, []);

  // Format date to "Month Day, Year" format
  const formatDate = useCallback(dateValue => {
    if (!dateValue) return 'No date';
    
    let date;
    if (typeof dateValue === 'string') {
      // Handle YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        const [y, m, d] = dateValue.split('-').map(Number);
        date = new Date(y, m - 1, d);
      } else {
        date = new Date(dateValue);
      }
    } else if (dateValue.seconds) {
      // Handle Firestore timestamp
      date = new Date(dateValue.seconds * 1000);
    } else {
      date = new Date(dateValue);
    }
    
    if (isNaN(date)) return 'Invalid date';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, []);

  const isLive = useCallback(event => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const rawStart = event.liveDate || event.startDate || event.endDate;
    const rawEnd = event.endDate || event.startDate || event.liveDate;
    const start = parseDate(rawStart);
    const end = parseDate(rawEnd);
    if (!start || !end) return false;
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return today >= start && today <= end;
  }, [parseDate]);

  const handleGenerateVoucher = async (event, client) => {
    let cleanDate = 'Invalid Date';
    let year = '';
    const rawDate = event.endDate?.seconds ? new Date(event.endDate.seconds * 1000) : new Date(`${event.endDate}T00:00:00`);

    if (!isNaN(rawDate)) {
      const day = rawDate.getDate();
      year = rawDate.getFullYear();
      const month = rawDate.toLocaleString('en-US', { month: 'long' });

      const getOrdinal = (n) => {
        if (n > 3 && n < 21) return 'th';
        switch (n % 10) {
          case 1: return 'st';
          case 2: return 'nd';
          case 3: return 'rd';
          default: return 'th';
        }
      };

      cleanDate = `${month} ${day}${getOrdinal(day)}, ${year}`;
    }

    const cleanedName = (client?.name || '')
      .replace(/\n|\r/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const scale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = 612 * scale;
    canvas.height = 792 * scale;
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);

    const today = new Date();
    const formattedDate = `${(today.getMonth() + 1).toString().padStart(2, '0')}${today.getDate().toString().padStart(2, '0')}${today.getFullYear()}`;

    const background = new Image();
    background.crossOrigin = "anonymous";
    background.onload = () => {
      ctx.drawImage(background, 0, 0, 612, 792);
      const numericDate = `${(rawDate.getMonth() + 1).toString().padStart(2, '0')}/${rawDate.getDate().toString().padStart(2, '0')}/${year}`;
      ctx.fillStyle = 'black';
      ctx.textAlign = 'right';
      ctx.font = 'bold 18px Arial';
      ctx.fillText(`EXPIRES: ${numericDate}`, 600, 280);
      ctx.fillStyle = 'black';
      ctx.font = 'bold 26px "Capellina Caps", serif';
      ctx.textAlign = 'center';
      ctx.fillText(`Expires: ${cleanDate}`, 306, 141);

      if (client?.blackLogo) {
        const logo = new Image();
        logo.crossOrigin = 'anonymous';
        logo.onload = () => {
          const dataUrl = canvas.toDataURL('image/png');
          const a = document.createElement('a');
          a.href = dataUrl;
          a.download = `${cleanedName}_${formattedDate}.png`;
          a.click();
        };
        logo.src = client.blackLogo;
      } else {
        const dataUrl = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `${event.jobName || 'voucher'}.png`;
        a.click();
      }
    };
    background.src = client?.voucherTemplate || `${window.location.origin}/voucher.jpg`;
  };

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    (async () => {
      const uid = user.uid;

      // 1) First get the client document by matching the user's UID to the authUid field
      const clientQuery = query(collection(db, 'clients'), where('authUid', '==', uid));
      const clientSnap = await getDocs(clientQuery);
      
      let actualClientDocId = null;
      let clientData = null;

      if (!clientSnap.empty) {
        const clientDoc = clientSnap.docs[0];
        actualClientDocId = clientDoc.id; // This is the Firestore document ID
        clientData = clientDoc.data();
        setClientDocId(actualClientDocId);


        // Handle logo URL
        const rawLogo = clientData.blackLogo;
        if (rawLogo?.startsWith('http')) {
          setClientData({ ...clientData, logoUrl: rawLogo });
        } else if (rawLogo) {
          const storage = getStorage();
          getDownloadURL(ref(storage, rawLogo))
            .then(url => setClientData({ ...clientData, logoUrl: url }))
            .catch(err => {
              console.error('Error fetching logo URL:', err);
              setClientData(clientData);  
            });
        } else {
          setClientData(clientData);
        }
      } else {
        console.log(`No client found with authUid: ${uid}`);
        setLoading(false);
        return;
      }

      // 2) Now fetch orders using the authUid instead of the Firestore document ID
      if (uid && actualClientDocId) {
        // Try both approaches for backward compatibility
        // First try with authUid (new approach)
        const ordersQueryNew = query(
          collection(db, 'orders'), 
          where('clientId', '==', uid)
        );
        const ordersSnapNew = await getDocs(ordersQueryNew);
        let allOrders = ordersSnapNew.docs.map(d => ({ id: d.id, ...d.data() }));

        // If no orders found with authUid, try with document ID (old approach)
        if (allOrders.length === 0) {
          const ordersQueryOld = query(
            collection(db, 'orders'), 
            where('clientId', '==', actualClientDocId)
          );
          const ordersSnapOld = await getDocs(ordersQueryOld);
          allOrders = ordersSnapOld.docs.map(d => ({ id: d.id, ...d.data() }));
        } else {
        }

        // Sort orders by createdAt date, newest first
        allOrders.sort((a, b) => {
          const dateA = a.createdAt?.seconds ? new Date(a.createdAt.seconds * 1000) : new Date(a.createdAt || 0);
          const dateB = b.createdAt?.seconds ? new Date(b.createdAt.seconds * 1000) : new Date(b.createdAt || 0);
          return dateB - dateA; // Newest first
        });

        setOrders(allOrders);

        // 3) Filter orders needing client approval - UPDATED LOGIC
        // Exclude orders that are already complete or approved
        const completedStatuses = [
          'Art Complete - No Insurance',
          'Art Complete - No Prizeboard',
          'Pending Prizeboard Approval',
          'Order Complete'
        ];
        
        const needingApproval = allOrders.filter(order => {
          // Don't show for approval if status is complete
          if (completedStatuses.includes(order.status)) {
            return false;
          }
          
          // Show for approval if status is explicitly pending approval
          return order.status === 'Pending Client Approval' || 
                 (order.adminApproval === 'Approved' && order.clientApproval === 'Pending');
        });
        
        setPendingApprovals(needingApproval);

        // 4) Fetch Facebook Events for this client using authUid
        // Try both approaches for backward compatibility
        const eventsQueryNew = query(
          collection(db, 'facebookEvents'),
          where('clientId', '==', uid)
        );
        const eventsSnapNew = await getDocs(eventsQueryNew);
        let allEvents = eventsSnapNew.docs.map(d => ({ id: d.id, ...d.data() }));
        
        // If no events found with authUid, try with document ID (old approach)
        if (allEvents.length === 0) {
          const eventsQueryOld = query(
            collection(db, 'facebookEvents'),
            where('clientId', '==', actualClientDocId)
          );
          const eventsSnapOld = await getDocs(eventsQueryOld);
          allEvents = eventsSnapOld.docs.map(d => ({ id: d.id, ...d.data() }));
         
        } else {
        }
        
        
        // Filter to only LIVE events (not just current) - using exact same logic as FacebookEventsPage
        const liveEvents = allEvents.filter(event => {
          const isActive = event.active !== false;
          const isEventLive = isLive(event);
          return isActive && isEventLive;
        });
      
        setFacebookEvents(liveEvents);
      }

      setLoading(false);
    })();
  }, [isLive]);

// Updated handleClientApproval function with system messaging
const handleClientApproval = async (orderId, decision, comment = '') => {
  try {
    // Update the order
    await updateDoc(doc(db, 'orders', orderId), {
      clientApproval: decision,
      clientComment: comment,
      status: decision === 'Approved' ? 'Art Complete - No Insurance' : 'Rejected by Client',
      updatedAt: new Date()
    });

    // Find the order to get details for the message
    const order = orders.find(o => o.id === orderId);
    const jobNumber = order?.jobNumber || orderId;

    // Send system message about the approval decision
    await sendSystemMessage(orderId, jobNumber, decision, comment);

    // Update local state
    setOrders(prev => prev.map(order => 
      order.id === orderId 
        ? { ...order, clientApproval: decision, status: decision === 'Approved' ? 'Art Complete - No Insurance' : 'Rejected by Client' }
        : order
    ));

    setPendingApprovals(prev => prev.filter(order => order.id !== orderId));

    alert(`Order ${decision.toLowerCase()} successfully!`);
  } catch (error) {
    console.error('Error updating approval:', error);
    alert('Failed to update approval');
  }
};

// Add this new function to send system messages
const sendSystemMessage = async (orderId, jobNumber, decision, comment) => {
  if (!clientDocId || !orderId) return;

  try {
    // Get dealership name from clientData
    const dealershipName = clientData?.name || '';

    // Create message text based on decision
    let messageText = `Job #${jobNumber}: Client ${decision.toLowerCase()} design`;
    if (comment) {
      messageText += ` - Comment: "${comment}"`;
    }

    const message = {
      sender: "client",
      senderName: dealershipName || "Client",
      senderRole: "client",
      recipientType: "internal",
      text: messageText,
      createdAt: new Date(),
      read: false,
      clientId: clientDocId,
      orderId: orderId,
      orderNumber: jobNumber,
      campaignName: '', // Could get this from order if needed
      dealershipName
    };

    // Add to messages collection
    await addDoc(collection(db, "clients", clientDocId, "messages"), message);
    
    console.log(`System message sent: ${messageText}`);
  } catch (error) {
    console.error('Error sending system message:', error);
  }
};

  if (loading) return <p className="client-dash-loading-state">Loading…</p>;

  return (
    <div className="client-dash-main-wrapper">
      {/* ─── Header with Logo and Title - REDESIGNED */}
      {clientData && (
        <div className="client-dash-header-section">
          {clientData.logoUrl && (
            <img
              src={clientData.logoUrl}
              alt={`${clientData.name} logo`}
              className="client-dash-logo-image"
            />
          )}
          <h1 className="client-dash-main-title">
            {clientData.name} Dashboard
          </h1>
        </div>
      )}

      {/* ─── Row 1: Priority Approvals Section ─── */}
      {pendingApprovals.length > 0 && (
        <div className="client-dash-priority-wrapper">
          <h3 className="client-dash-priority-heading">
            🔔 Designs Waiting for Your Approval ({pendingApprovals.length})
          </h3>
          
          <div className="client-dash-approvals-layout">
            {pendingApprovals.map(order => (
              <ApprovalCard 
                key={order.id} 
                order={order} 
                onApproval={handleClientApproval}
                formatDate={formatDate}
              />
            ))}
          </div>
        </div>
      )}

      {/* ─── Row 2: Messages Section ─── */}
      <div className="client-dash-messages-grid">
        <div className="client-dash-message-panel">
          <h3 className="client-dash-message-panel-title">Send Message</h3>
          <MessageForm clientId={clientDocId} recipientType="admin" />
        </div>
        <div className="client-dash-message-panel">
          <h3 className="client-dash-message-panel-title">Recent Messages</h3>
          <MessageLog clientId={clientDocId} recipientType="client" />
        </div>
      </div>

      {/* ─── Row 3: Direct Mail Orders (Full Width) ─── */}
      <div className="client-dash-info-panel" style={{ marginBottom: '20px' }}>
        <h4 className="client-dash-info-panel-title">Direct Mail Orders</h4>
        {orders.length === 0 ? (
          <p className="client-dash-info-panel-text">No Direct Mail Orders</p>
        ) : (
          <ul className="client-dash-orders-listing">
            {orders.slice(0, 10).map(order => (
              <OrderSummaryCard 
                key={order.id} 
                order={order} 
                onApproval={handleClientApproval}
                formatDate={formatDate}
              />
            ))}
            {orders.length > 10 && (
              <li className="client-dash-more-orders-note">
                <p>... and {orders.length - 10} more orders</p>
              </li>
            )}
          </ul>
        )}
      </div>

      {/* ─── Row 4: Two Column Layout - Analytics and Facebook Events ─── */}
      <div className="client-dash-split-layout">
        {/* Analytics Card */}
        <div className="client-dash-info-panel">
          <h4 className="client-dash-info-panel-title">Client Information</h4>
          {clientData ? (
            <div className="client-dash-client-info-content">
              <div className="client-dash-info-row">
                <span className="client-dash-info-label">GM:</span>
                <span className="client-dash-info-value">{clientData.generalManager || 'Not set'}</span>
              </div>
              
              <div className="client-dash-info-row">
                <span className="client-dash-info-label">Phone:</span>
                <span className="client-dash-info-value">{clientData.phone || 'Not set'}</span>
              </div>
              
              <div className="client-dash-info-row">
                <span className="client-dash-info-label">Email:</span>
                <span className="client-dash-info-value">{clientData.email || 'Not set'}</span>
              </div>
              
              <div className="client-dash-info-row">
                <span className="client-dash-info-label">Manufacturer:</span>
                <span className="client-dash-info-value">{clientData.manufacturer || 'Not set'}</span>
              </div>
              
              <div className="client-dash-info-row">
                <span className="client-dash-info-label">Website:</span>
                <span className="client-dash-info-value">
                  {clientData.dealershipWebsite ? (
                    <a 
                      href={clientData.dealershipWebsite.startsWith('http') ? clientData.dealershipWebsite : `https://${clientData.dealershipWebsite}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#007bff', textDecoration: 'underline' }}
                    >
                      {clientData.dealershipWebsite}
                    </a>
                  ) : (
                    'Not set'
                  )}
                </span>
              </div>
              
              <div className="client-dash-info-row">
                <span className="client-dash-info-label">Address:</span>
                <span className="client-dash-info-value">
                  {[clientData.street1, clientData.street2, clientData.city, clientData.state, clientData.zip]
                    .filter(Boolean)
                    .join(', ') || 'Not set'}
                </span>
              </div>
              
              <div className="client-dash-info-row">
                <span className="client-dash-info-label">Facebook Verbiage Disclaimer Link:</span>
                <span className="client-dash-info-value">
                  {clientData.disclaimerLink ? (
                    <a 
                      href={clientData.disclaimerLink.startsWith('http') ? clientData.disclaimerLink : `https://${clientData.disclaimerLink}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#007bff', textDecoration: 'underline' }}
                    >
                      {clientData.disclaimerLink}
                    </a>
                  ) : (
                    'Not set'
                  )}
                </span>
              </div>
              
              {/* DealerApps Login Section */}
              <div className="client-dash-dealerapps-section">
                <div className="client-dash-dealerapps-header">
                  <h5 className="client-dash-dealerapps-title">DealerApps Login</h5>
                  <span className="client-dash-info-value">Leads & Reports</span>
                  <a 
                    href="https://srmgroup.dealerapps.com/srmgroup?"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="client-dash-dealerapps-link"
                  >
                    srmgleads.com →
                  </a>
                </div>
                
                <div className="client-dash-info-row">
                  <span className="client-dash-info-label">Client DealerApps Username:</span>
                  <span className="client-dash-info-value">{clientData.clientDealerAppsUsername || 'None set'}</span>
                </div>
                
                <div className="client-dash-info-row">
                  <span className="client-dash-info-label">Client DealerApps Password:</span>
                  <span className="client-dash-info-value">{clientData.clientDealerAppsPassword || 'None set'}</span>
                </div>
              </div>
            </div>
          ) : (
            <p className="client-dash-info-panel-text">Client information not available</p>
          )}
        </div>

        {/* Live Facebook Events Card */}
        <div className="client-dash-info-panel">
          <h4 className="client-dash-info-panel-title">Live Facebook Events</h4>
          {facebookEvents.length === 0 ? (
            <p className="client-dash-info-panel-text">No Live Events</p>
          ) : (
            <ul className="client-dash-events-listing">
              {facebookEvents.map(event => (
                <li key={event.id} className="client-dash-event-card">
                  <p className="client-dash-event-name-text">
                    <strong>{event.jobName || event.id}</strong>
                  </p>
                  <p className="client-dash-event-dates-text">
                    {formatDate(event.startDate)} → {formatDate(event.endDate)}
                  </p>
                  <p className="client-dash-event-dates-text">
                    Live: {formatDate(event.liveDate)}
                  </p>
                  <div className="client-dash-event-button-area">
                    <button
                      onClick={() => handleGenerateVoucher(event, clientData)}
                      className="client-dash-voucher-download-btn"
                    >
                      Download Voucher
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// Order Summary Card Component (for the list view)
function OrderSummaryCard({ order, onApproval, formatDate }) {
  const [showDesign, setShowDesign] = useState(false);
  const [selectedMailerProof, setSelectedMailerProof] = useState(null);
  const [selectedPrizeboardProof, setSelectedPrizeboardProof] = useState(null);
  const [comment, setComment] = useState('');

  // Statuses that should show design without approval
  const completedStatuses = [
    'Art Complete - No Insurance',
    'Art Complete - No Prizeboard',
    'Pending Prizeboard Approval',
    'Order Complete'
  ];
  const isCompleted = completedStatuses.includes(order.status);

  useEffect(() => {
    // Fetch proofs if order is pending approval or completed
    if (order.clientApproval === 'Pending' || order.clientApproval === 'Approved' || isCompleted) {
      const fetchProofs = async () => {
        try {
          const proofsSnap = await getDocs(collection(db, 'orders', order.id, 'proofs'));
          const proofs = proofsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          // Separate mailer and prizeboard proofs
          const mailerProofs = proofs.filter(proof => proof.proofType === 'mailer' || !proof.proofType);
          const prizeboardProofs = proofs.filter(proof => proof.proofType === 'prizeboard');
          
          if (mailerProofs.length > 0) {
            setSelectedMailerProof(mailerProofs[0]);
          }
          if (prizeboardProofs.length > 0) {
            setSelectedPrizeboardProof(prizeboardProofs[0]);
          }
        } catch (error) {
          console.error('Error fetching proofs:', error);
        }
      };
      fetchProofs();
    }
  }, [order.id, order.clientApproval, order.status, isCompleted]);

  const getDropDatesDisplay = () => {
    if (!order.drops || order.drops.length === 0) return 'No drop dates set';
    
    return order.drops.map((drop, index) => (
      <div key={index} className="client-dash-single-drop-info">
        Drop {index + 1}: {formatDate(drop.startDate)} - {formatDate(drop.endDate)}
        {drop.quantity && ` (${drop.quantity} pieces)`}
      </div>
    ));
  };

  const getStatusBadge = () => {
    if (order.clientApproval === 'Pending' && !isCompleted) {
      return (
        <span className="client-dash-status-badge client-dash-status-pending">
          ⏰ Approval Needed
        </span>
      );
    }
    if (order.clientApproval === 'Approved' || isCompleted) {
      return (
        <span className="client-dash-status-badge client-dash-status-approved">
          ✅ {order.status || 'Approved'}
        </span>
      );
    }
    return null;
  };

  return (
    <li className={`client-dash-order-entry ${order.clientApproval === 'Pending' && !isCompleted ? 'client-dash-needs-approval' : ''}`}>
      <div className="client-dash-order-summary">
        <div className="client-dash-order-header-row">
          <div>
            <p className="client-dash-job-number-text">
              Job #{order.jobNumber || order.id}
            </p>
            <p className="client-dash-created-date-text">
              Created: {order.createdAt?.toDate ? formatDate(order.createdAt.toDate()) : formatDate(order.createdAt)}
            </p>
          </div>
          {getStatusBadge()}
        </div>
        
        {/* Drop Dates */}
        <div className="client-dash-drops-info-wrapper">
          <strong className="client-dash-drops-label-text">Drop Dates:</strong>
          <div style={{ marginTop: '4px' }}>
            {getDropDatesDisplay()}
          </div>
        </div>
      </div>

      {/* Show approval interface if pending and not completed */}
      {order.clientApproval === 'Pending' && !isCompleted && (selectedMailerProof || selectedPrizeboardProof) && (
        <div className="client-dash-design-area">
          <div style={{ marginBottom: '12px' }}>
            <strong className="client-dash-design-area-label">Design Preview:</strong>
            
            {/* Display mailer and prizeboard side by side if both exist */}
            <div className={selectedMailerProof && selectedPrizeboardProof ? 'client-dash-proof-grid' : ''}>
              {selectedMailerProof && (
                <div className="client-dash-proof-container">
                  <h5 className="client-dash-proof-type-label">Mailer Design</h5>
                  <div className="client-dash-large-iframe-box">
                    <iframe
                      src={selectedMailerProof.fileUrl}
                      className="client-dash-embedded-iframe"
                      title="Mailer Design Preview"
                    />
                  </div>
                  <a
                    href={selectedMailerProof.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="client-dash-view-full-link"
                  >
                    View Full Size
                  </a>
                </div>
              )}
              
              {selectedPrizeboardProof && (
                <div className="client-dash-proof-container">
                  <h5 className="client-dash-proof-type-label">Prizeboard Design</h5>
                  <div className="client-dash-large-iframe-box">
                    <iframe
                      src={selectedPrizeboardProof.fileUrl}
                      className="client-dash-embedded-iframe"
                      title="Prizeboard Design Preview"
                    />
                  </div>
                  <a
                    href={selectedPrizeboardProof.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="client-dash-view-full-link"
                  >
                    View Full Size
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="client-dash-order-comment-area">
            <label className="client-dash-order-comment-label">
              Comments (optional):
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add any feedback or comments..."
              className="client-dash-order-comment-input"
            />
          </div>

          <div className="client-dash-order-action-row">
            <button
              onClick={() => onApproval(order.id, 'Approved', comment)}
              className="client-dash-order-action-button client-dash-order-approve-button"
            >
              ✅ Approve
            </button>
            
            <button
              onClick={() => onApproval(order.id, 'Rejected', comment)}
              className="client-dash-order-action-button client-dash-order-reject-button"
            >
              ❌ Request Changes
            </button>
          </div>
        </div>
      )}

      {/* Show design if approved or completed - no approval buttons */}
      {(order.clientApproval === 'Approved' || isCompleted) && (selectedMailerProof || selectedPrizeboardProof) && (
        <div className="client-dash-design-area">
          <button
            onClick={() => setShowDesign(!showDesign)}
            className="client-dash-toggle-design-btn"
          >
            {showDesign ? 'Hide Design' : 'View Design'}
          </button>
          
          {showDesign && (
            <div style={{ marginTop: '8px' }}>
              <div className={selectedMailerProof && selectedPrizeboardProof ? 'client-dash-proof-grid' : ''}>
                {selectedMailerProof && (
                  <div className="client-dash-proof-container">
                    <h5 className="client-dash-proof-type-label">Mailer Design</h5>
                    <div className="client-dash-small-iframe-box">
                      <iframe
                        src={selectedMailerProof.fileUrl}
                        className="client-dash-embedded-iframe"
                        title="Mailer Design"
                      />
                    </div>
                    <a
                      href={selectedMailerProof.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="client-dash-view-full-link"
                    >
                      View Full Size
                    </a>
                  </div>
                )}
                
                {selectedPrizeboardProof && (
                  <div className="client-dash-proof-container">
                    <h5 className="client-dash-proof-type-label">Prizeboard Design</h5>
                    <div className="client-dash-small-iframe-box">
                      <iframe
                        src={selectedPrizeboardProof.fileUrl}
                        className="client-dash-embedded-iframe"
                        title="Prizeboard Design"
                      />
                    </div>
                    <a
                      href={selectedPrizeboardProof.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="client-dash-view-full-link"
                    >
                      View Full Size
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

// Full Approval Card Component (for priority section)
function ApprovalCard({ order, onApproval, formatDate }) {
  const [comment, setComment] = useState('');
  const [selectedMailerProof, setSelectedMailerProof] = useState(null);
  const [selectedPrizeboardProof, setSelectedPrizeboardProof] = useState(null);

  useEffect(() => {
    // Fetch proofs for this order
    const fetchProofs = async () => {
      try {
        const proofsSnap = await getDocs(collection(db, 'orders', order.id, 'proofs'));
        const proofs = proofsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Separate mailer and prizeboard proofs
        const mailerProofs = proofs.filter(proof => proof.proofType === 'mailer' || !proof.proofType);
        const prizeboardProofs = proofs.filter(proof => proof.proofType === 'prizeboard');
        
        if (mailerProofs.length > 0) {
          setSelectedMailerProof(mailerProofs[0]);
        }
        if (prizeboardProofs.length > 0) {
          setSelectedPrizeboardProof(prizeboardProofs[0]);
        }
      } catch (error) {
        console.error('Error fetching proofs:', error);
      }
    };

    fetchProofs();
  }, [order.id]);

  const getDropDatesDisplay = () => {
    if (!order.drops || order.drops.length === 0) return 'No drop dates set';
    
    return order.drops.map((drop, index) => (
      <div key={index} className="client-dash-drop-date-line">
        <strong>Drop {index + 1}:</strong> {formatDate(drop.startDate)} - {formatDate(drop.endDate)}
        {drop.quantity && ` (${drop.quantity} pieces)`}
      </div>
    ));
  };

  return (
    <div className="client-dash-approval-box">
      <h4 className="client-dash-approval-heading">
        Job #{order.jobNumber}
      </h4>

      {/* Drop Dates */}
      <div className="client-dash-drop-info-section">
        <h5 className="client-dash-drop-info-title">Drop Dates:</h5>
        {getDropDatesDisplay()}
      </div>
      
      {/* Display mailer and prizeboard side by side if both exist */}
      {(selectedMailerProof || selectedPrizeboardProof) && (
        <div className="client-dash-design-preview-section">
          <h5 className="client-dash-design-preview-title">Design Preview:</h5>
          
          <div className={selectedMailerProof && selectedPrizeboardProof ? 'client-dash-proof-grid' : ''}>
            {selectedMailerProof && (
              <div className="client-dash-proof-container">
                <h6 className="client-dash-proof-type-label">Mailer Design</h6>
                <div className="client-dash-iframe-wrapper">
                  <iframe
                    src={selectedMailerProof.fileUrl}
                    className="client-dash-preview-iframe"
                    title="Mailer Design Preview"
                  />
                </div>
                <a
                  href={selectedMailerProof.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="client-dash-fullsize-anchor"
                >
                  View Full Size
                </a>
              </div>
            )}
            
            {selectedPrizeboardProof && (
              <div className="client-dash-proof-container">
                <h6 className="client-dash-proof-type-label">Prizeboard Design</h6>
                <div className="client-dash-iframe-wrapper">
                  <iframe
                    src={selectedPrizeboardProof.fileUrl}
                    className="client-dash-preview-iframe"
                    title="Prizeboard Design Preview"
                  />
                </div>
                <a
                  href={selectedPrizeboardProof.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="client-dash-fullsize-anchor"
                >
                  View Full Size
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="client-dash-comment-input-section">
        <label className="client-dash-comment-input-label">
          Comments (optional):
        </label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add any feedback or comments..."
          className="client-dash-comment-textarea-field"
        />
      </div>

      <div className="client-dash-approval-actions">
        <button
          onClick={() => onApproval(order.id, 'Approved', comment)}
          className="client-dash-action-btn client-dash-approve-btn"
        >
          ✅ Approve
        </button>
        
        <button
          onClick={() => onApproval(order.id, 'Rejected', comment)}
          className="client-dash-action-btn client-dash-reject-btn"
        >
          ❌ Request Changes
        </button>
      </div>
    </div>
  );
}